import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trophy, Users, Calendar, Plus, Target, Clock, Loader2, AlertCircle, X, Search, Upload, Image, FileStack, Crown, List, BarChart2, LayoutTemplate, Check } from 'lucide-react'
import { tournamentsAPI, authAPI, teamImagesAPI, statsAPI, teamsAPI } from '@/lib/api'
import TournamentSetupWizard from '@/components/TournamentSetupWizard'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import ImageCropper from '@/components/ImageCropper'

import PageHeader from '@/components/PageHeader'
import SubNav from '@/components/SubNav'
import { useAuth } from '@/hooks/useAuth'
import {  } from 'lucide-react'

// formatTypeLabel wordt geëxporteerd uit lib/tournamentFormats — enkelvoudig punt van waarheid.
import { formatLabel as formatTypeLabel } from '@/lib/tournamentFormats'

export default function TournamentsPage() {
  const { isAdmin: _isAdmin } = useAuth()

  const navigate = useNavigate()

  // State management
  const [tournaments, setTournaments] = useState([])
  const [wizardOpen, setWizardOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState('all')
  // Scope-toggle: 'active' = huidige LAN-editie (default), 'archived' = vorige
  // edities, 'all' = alles bij elkaar. Vervangt het gedrag waarbij álle
  // tournaments altijd zichtbaar waren — nu blijft de lijst rustig.
  const [scope, setScope] = useState('active')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)

  // Dialog states
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState(null)
  const [teamName, setTeamName] = useState('')
  const [teamMembers, setTeamMembers] = useState([])
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [registerLoading, setRegisterLoading] = useState(false)
  const [registerError, setRegisterError] = useState(null)
  const [registerSuccess, setRegisterSuccess] = useState(false)
  // Persistent teams — user kan bij inschrijving kiezen om een bestaand
  // team te gebruiken. Als geselecteerd wordt de team-naam vastgezet en
  // toont de leden-picker enkel de leden van dat team.
  const [existingTeams, setExistingTeams]         = useState([])
  const [selectedTeamId, setSelectedTeamId]       = useState(null)
  const [teamsMembersOfPicked, setTeamsMembers]   = useState([])
  // Users die al ingeschreven zijn voor dit toernooi (via team of solo)
  // — we blokkeren die in de picker zodat je niet iemand kan aanvinken die al
  // bij een ander team meespeelt.
  const [alIngeschreven, setAlIngeschreven]       = useState({ ids: new Set(), byUser: {} })
  const [teamLogo, setTeamLogo] = useState(null)
  const [teamBanner, setTeamBanner] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)

  // Image cropper states
  const [logoCropperOpen, setLogoCropperOpen] = useState(false)
  const [bannerCropperOpen, setBannerCropperOpen] = useState(false)
  const [originalLogoImage, setOriginalLogoImage] = useState(null)
  const [originalBannerImage, setOriginalBannerImage] = useState(null)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [createForm, setCreateForm] = useState({
    name: '',
    game: '',
    type: 'pending',
    registration_type: 'personal',
    scoring_type: 'kills',
    rounds: '1',
    max_participants: '',
    team_size: '',
    description: '',
    rules: '',
    start_time: '',
  })

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [tournamentDetails, setTournamentDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const response = await authAPI.getCurrentUser()
          setCurrentUser(response.data.user)
        }
      } catch (err) {
        console.error('Failed to fetch current user:', err)
        // User not logged in
        setCurrentUser(null)
      }
    }
    fetchCurrentUser()
  }, [])

  // Fetch tournaments
  const fetchTournaments = async (status = null, scopeArg = scope) => {
    try {
      setLoading(true)
      setError(null)
      const params = { scope: scopeArg }
      if (status && status !== 'all') {
        params.status = status
      }
      const response = await tournamentsAPI.getTournaments(params)
      setTournaments(response.data.tournaments || [])
    } catch (err) {
      console.error('Failed to fetch tournaments:', err)
      setError(err.response?.data?.error || 'Failed to load tournaments')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTournaments(selectedTab, scope)
  }, [selectedTab, scope])

  // Handle tab change
  const handleTabChange = (value) => {
    setSelectedTab(value)
  }

  // Search users for team selection
  useEffect(() => {
    const searchUsers = async () => {
      if (!userSearchQuery || userSearchQuery.length < 2) {
        setUserSearchResults([])
        return
      }

      try {
        setSearchingUsers(true)
        const response = await authAPI.searchUsers(userSearchQuery)
        setUserSearchResults(response.data.users || [])
      } catch (err) {
        console.error('User search failed:', err)
        setUserSearchResults([])
      } finally {
        setSearchingUsers(false)
      }
    }

    const debounceTimer = setTimeout(searchUsers, 300)
    return () => clearTimeout(debounceTimer)
  }, [userSearchQuery])

  // Handle registration
  const handleRegisterClick = (tournament) => {
    if (!currentUser) {
      setError('Please log in to register for tournaments')
      return
    }
    setSelectedTournament(tournament)
    setRegisterDialogOpen(true)
    setTeamName('')
    setTeamMembers([])
    setUserSearchQuery('')
    setUserSearchResults([])
    setRegisterError(null)
    setRegisterSuccess(false)
    setTeamLogo(null)
    setTeamBanner(null)
    setLogoPreview(null)
    setBannerPreview(null)
    setSelectedTeamId(null)
    setTeamsMembers([])
    // Persistente teams tonen om te kiezen uit — enkel voor team-toernooi zinvol.
    // Deelnemers zien enkel teams waar ze lid van zijn (anders kunnen ze
    // niet inschrijven — backend blokkeert dat). Admin/superadmin ziet
    // alles zodat walk-in-registratie voor een willekeurig team werkt.
    if (tournament.registration_type === 'team') {
      const magAlles = ['admin', 'superadmin'].includes(currentUser?.role)
      teamsAPI.list()
        .then(r => {
          const alle = r.data?.teams ?? []
          setExistingTeams(magAlles ? alle : alle.filter(t => t.is_member))
        })
        .catch(() => setExistingTeams([]))
    } else {
      setExistingTeams([])
    }
    // Ophalen wie al ingeschreven is (via team_members of solo) — voor blokkade in picker.
    tournamentsAPI.registeredUsers(tournament.id)
      .then(r => setAlIngeschreven({
        ids: new Set(r.data?.user_ids ?? []),
        byUser: r.data?.by_user ?? {},
      }))
      .catch(() => setAlIngeschreven({ ids: new Set(), byUser: {} }))
  }

  // Als een persistent team wordt gekozen: naam vastzetten + leden laden.
  const kiesBestaandTeam = async (teamId) => {
    if (!teamId) {
      setSelectedTeamId(null)
      setTeamsMembers([])
      setTeamMembers([])
      return
    }
    try {
      const r = await teamsAPI.get(teamId)
      setSelectedTeamId(teamId)
      setTeamName(r.data?.team?.name ?? '')
      setTeamsMembers(r.data?.members ?? [])
      setTeamMembers([])   // start met lege line-up; user vinkt aan wie meedoet
    } catch (e) { setRegisterError(e.response?.data?.error ?? e.message) }
  }

  const handleAddTeamMember = (user) => {
    if (teamMembers.find(m => m.id === user.id)) {
      setRegisterError('User already added to team')
      return
    }
    setTeamMembers([...teamMembers, user])
    setUserSearchQuery('')
    setUserSearchResults([])
    setRegisterError(null)
  }

  const handleRemoveTeamMember = (userId) => {
    setTeamMembers(teamMembers.filter(m => m.id !== userId))
  }

  const handleLogoSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file size (5MB max for original)
    if (file.size > 5 * 1024 * 1024) {
      setRegisterError('Image file size must be less than 5MB')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setRegisterError('Only image files are allowed')
      return
    }

    // Open cropper with original image
    setOriginalLogoImage(URL.createObjectURL(file))
    setLogoCropperOpen(true)
    setRegisterError(null)

    // Reset file input
    e.target.value = ''
  }

  const handleLogoCropComplete = (croppedBlob) => {
    const croppedFile = new File([croppedBlob], 'logo.jpg', { type: 'image/jpeg' })
    setTeamLogo(croppedFile)
    setLogoPreview(URL.createObjectURL(croppedBlob))
  }

  const handleBannerSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file size (5MB max for original)
    if (file.size > 5 * 1024 * 1024) {
      setRegisterError('Image file size must be less than 5MB')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setRegisterError('Only image files are allowed')
      return
    }

    // Open cropper with original image
    setOriginalBannerImage(URL.createObjectURL(file))
    setBannerCropperOpen(true)
    setRegisterError(null)

    // Reset file input
    e.target.value = ''
  }

  const handleBannerCropComplete = (croppedBlob) => {
    const croppedFile = new File([croppedBlob], 'banner.jpg', { type: 'image/jpeg' })
    setTeamBanner(croppedFile)
    setBannerPreview(URL.createObjectURL(croppedBlob))
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()

    // Bij team-toernooi: verplicht een persistent team kiezen (geen vrije invoer).
    if (selectedTournament?.registration_type === 'team' && !selectedTeamId) {
      setRegisterError('Kies een bestaand team uit de lijst hierboven, of maak er eerst een aan op /teams')
      return
    }

    // Validate team members for team tournaments
    if (selectedTournament?.registration_type === 'team' && teamMembers.length === 0) {
      setRegisterError('Vink minstens één speler aan uit het team')
      return
    }

    // Bij vast team_size moet exact dat aantal gekozen zijn.
    const vereist = selectedTournament?.team_size
    if (vereist && teamMembers.length !== vereist) {
      setRegisterError(`Dit toernooi vraagt exact ${vereist} spelers per team (je hebt er ${teamMembers.length} gekozen)`)
      return
    }

    try {
      setRegisterLoading(true)
      setRegisterError(null)

      // For personal tournaments, use display name if provided, otherwise backend will use username
      const displayName = teamName.trim() || (currentUser?.display_name || currentUser?.username)
      const payload = { team_name: displayName }

      if (selectedTournament?.registration_type === 'team') {
        payload.team_members = teamMembers.map(m => m.id)
      }
      // Als user een bestaand team gebruikt, geef dat mee zodat backend
      // de naam/logo daaruit oppikt en valideert dat leden erbij horen.
      if (selectedTeamId) {
        payload.team_id = selectedTeamId
      }

      const response = await tournamentsAPI.joinTournament(selectedTournament.id, payload)
      const participantId = response.data.participantId

      // Upload team logo if selected
      if (teamLogo && participantId) {
        try {
          const logoFormData = new FormData()
          logoFormData.append('logo', teamLogo)
          await teamImagesAPI.uploadLogo(participantId, logoFormData)
        } catch (logoError) {
          console.error('Logo upload failed:', logoError)
          // Don't fail registration if logo upload fails
        }
      }

      // Upload team banner if selected
      if (teamBanner && participantId) {
        try {
          const bannerFormData = new FormData()
          bannerFormData.append('banner', teamBanner)
          await teamImagesAPI.uploadBanner(participantId, bannerFormData)
        } catch (bannerError) {
          console.error('Banner upload failed:', bannerError)
          // Don't fail registration if banner upload fails
        }
      }

      setRegisterSuccess(true)
      setTimeout(() => {
        setRegisterDialogOpen(false)
        fetchTournaments(selectedTab, scope)
      }, 1500)
    } catch (err) {
      console.error('Registration failed:', err)
      setRegisterError(err.response?.data?.error || 'Registration failed')
    } finally {
      setRegisterLoading(false)
    }
  }

  // Handle create tournament
  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    if (!createForm.name || !createForm.game) {
      setCreateError('Naam en game zijn verplicht')
      return
    }

    try {
      setCreateLoading(true)
      setCreateError(null)
      const payload = {
        ...createForm,
        max_participants: createForm.max_participants ? parseInt(createForm.max_participants) : null,
        rounds: createForm.rounds ? parseInt(createForm.rounds) : 1,
        team_size: (createForm.registration_type === 'team' && createForm.team_size)
          ? parseInt(createForm.team_size) : null,
      }
      await tournamentsAPI.createTournament(payload)
      setCreateDialogOpen(false)
      setCreateForm({
        name: '',
        game: '',
        type: 'pending',
        registration_type: 'personal',
        scoring_type: 'kills',
        rounds: '1',
        max_participants: '',
        team_size: '',
        description: '',
        rules: '',
        start_time: '',
      })
      fetchTournaments(selectedTab, scope)
    } catch (err) {
      console.error('Create tournament failed:', err)
      setCreateError(err.response?.data?.error || 'Failed to create tournament')
    } finally {
      setCreateLoading(false)
    }
  }

  // Handle view details - navigate to dedicated page
  const handleViewDetails = (tournament) => {
    navigate(`/tournaments/${tournament.id}`)
  }

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'registration':
        return 'bg-neon-green/10 text-neon-green border-neon-green/30 hover:bg-neon-green/20'
      case 'in_progress':
        return 'bg-neon-blue/10 text-neon-blue border-neon-blue/30 hover:bg-neon-blue/20'
      case 'completed':
        return 'bg-neon-purple/10 text-neon-purple border-neon-purple/30 hover:bg-neon-purple/20'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'TBA'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Format tournament type — delegeert naar shared formatLabel.
  const formatType = (type) => {
    return formatTypeLabel(type)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader icon={Trophy} title="Toernooien" />
      <div className="mb-4">
        <SubNav isAdmin={_isAdmin} items={[
          { to: '/tournaments',             label: 'Overzicht',  icon: List },
          { to: '/tournaments/leaderboard', label: 'Ranglijst',  icon: BarChart2 },
          { to: '/tournaments/templates',   label: 'Templates',  icon: LayoutTemplate, adminOnly: true },
        ]} />
      </div>
          <p className="text-muted-foreground mt-1">
            Compete in organized gaming tournaments
          </p>
        </div>
        <Button asChild variant="outline" className="mr-2">
          <Link to="/tournaments/leaderboard"><Trophy className="h-4 w-4 mr-2" />Leaderboard</Link>
        </Button>
        {currentUser && ['admin','superadmin'].includes(currentUser?.role) && (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/tournaments/templates"><FileStack className="h-4 w-4 mr-2" />Templates</Link>
            </Button>
            <TournamentSetupWizard onCreated={fetchTournaments} />
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2 bg-acid/10 hover:bg-acid/20 text-acid border border-acid/30 transition-all">
                <Plus className="h-4 w-4" />
                <span>Nieuw toernooi</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[525px] border-acid/30">
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle className="text-acid">Nieuw toernooi</DialogTitle>
                  <DialogDescription>
                    Naam en game volstaan — de rest kies je later.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  {createError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{createError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid gap-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Toernooi-naam *
                    </label>
                    <Input
                      id="name"
                      value={createForm.name}
                      onChange={(e) => handleCreateChange('name', e.target.value)}
                      placeholder="Bear & Bockie Zomer 2026"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="game" className="text-sm font-medium">
                      Game *
                    </label>
                    <Input
                      id="game"
                      value={createForm.game}
                      onChange={(e) => handleCreateChange('game', e.target.value)}
                      placeholder="Counter-Strike 2"
                      required
                    />
                  </div>
                  <div className="rounded-md border border-acid/30 bg-acid/5 px-3 py-2 text-xs text-muted-foreground">
                    <span className="font-mono uppercase tracking-widest text-acid text-[10px] block mb-1">Format later</span>
                    Kies pas <em>na</em> de inschrijvingen welk toernooi-format geschikt is —
                    dan weet je zeker hoeveel spelers er echt zijn.
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="registration_type" className="text-sm font-medium">
                      Inschrijvings-modus *
                    </label>
                    <select
                      id="registration_type"
                      value={createForm.registration_type}
                      onChange={(e) => handleCreateChange('registration_type', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="personal">Individueel</option>
                      <option value="team">Team</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Individueel: spelers schrijven zichzelf in. Team: spelers schrijven als team in met meerdere leden.
                    </p>
                  </div>
                  {createForm.registration_type === 'team' && (
                    <div className="grid gap-2">
                      <label htmlFor="team_size" className="text-sm font-medium">
                        Teamgrootte (aantal spelers per team)
                      </label>
                      <Input
                        id="team_size"
                        type="number"
                        value={createForm.team_size}
                        onChange={(e) => handleCreateChange('team_size', e.target.value)}
                        placeholder="bv. 5"
                        min="1"
                      />
                      <p className="text-xs text-muted-foreground">
                        Leeg = flexibel aantal. Ingevuld = elk team moet exact zoveel spelers aanmelden.
                      </p>
                    </div>
                  )}
                  <div className="grid gap-2">
                    <label htmlFor="max_participants" className="text-sm font-medium">
                      Max. deelnemers
                    </label>
                    <Input
                      id="max_participants"
                      type="number"
                      value={createForm.max_participants}
                      onChange={(e) => handleCreateChange('max_participants', e.target.value)}
                      placeholder="16"
                      min="2"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="start_time" className="text-sm font-medium">
                      Starttijd
                    </label>
                    <Input
                      id="start_time"
                      type="datetime-local"
                      value={createForm.start_time}
                      onChange={(e) => handleCreateChange('start_time', e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="description" className="text-sm font-medium">
                      Omschrijving
                    </label>
                    <textarea
                      id="description"
                      value={createForm.description}
                      onChange={(e) => handleCreateChange('description', e.target.value)}
                      placeholder="Toernooi-details, prijzen, schema..."
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="rules" className="text-sm font-medium">
                      Regels
                    </label>
                    <textarea
                      id="rules"
                      value={createForm.rules}
                      onChange={(e) => handleCreateChange('rules', e.target.value)}
                      placeholder="Best of 3&#10;1 punt per kill&#10;Maps: Dust2, Mirage, Inferno&#10;15 minuten per ronde"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <p className="text-xs text-muted-foreground">
                      Vrije tekst — scoring, maps, tijdslimiet, alles wat handig is voor deelnemers.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setCreateDialogOpen(false)}
                    disabled={createLoading}
                  >
                    Annuleer
                  </Button>
                  <Button
                    type="submit"
                    disabled={createLoading}
                    className="bg-acid/10 hover:bg-acid/20 text-acid border border-acid/30"
                  >
                    {createLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Aanmaken…
                      </>
                    ) : (
                      'Toernooi aanmaken'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Scope-toggle: huidige LAN-editie, archief of alles. Standaard 'active'
          zodat toernooien van vorige edities de lijst niet vervuilen. */}
      <div className="flex items-center justify-end gap-1 text-xs">
        {[
          { key: 'active',   label: 'Huidige editie' },
          { key: 'archived', label: 'Archief' },
          { key: 'all',      label: 'Alles' },
        ].map(o => (
          <button key={o.key}
                  onClick={() => setScope(o.key)}
                  className={`px-3 py-1.5 rounded-md border transition-colors ${
                    scope === o.key
                      ? 'border-acid bg-acid/10 text-acid'
                      : 'border-border text-muted-foreground hover:border-acid/40'}`}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Filters using Tabs — vier onderscheiden token-kleuren zodat de
          statussen visueel te scheiden zijn (peach voor 'Bezig', gold
          voor 'Gedaan'). */}
      <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-card border border-border">
          <TabsTrigger
            value="all"
            className="data-[state=active]:bg-acid/10 data-[state=active]:text-acid"
          >
            Alles
          </TabsTrigger>
          <TabsTrigger
            value="registration"
            className="data-[state=active]:bg-live/10 data-[state=active]:text-live"
          >
            Inschrijving
          </TabsTrigger>
          <TabsTrigger
            value="in_progress"
            className="data-[state=active]:bg-peach/10 data-[state=active]:text-peach"
          >
            Bezig
          </TabsTrigger>
          <TabsTrigger
            value="completed"
            className="data-[state=active]:bg-gold/10 data-[state=active]:text-gold"
          >
            Gedaan
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedTab} className="mt-6">
          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-neon-pink" />
              <span className="ml-2 text-muted-foreground">Loading tournaments...</span>
            </div>
          ) : tournaments.length === 0 ? (
            /* Empty State */
            <Card className="border-neon-pink/20">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Tournaments Found</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  {selectedTab === 'all'
                    ? 'No tournaments available yet. Check back later or create one!'
                    : `No ${selectedTab.replace('_', ' ')} tournaments at the moment.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            /* tour-card-kabinet — één card per rij, kabinet-stijl uit mockup C.
               Bij 'Alles': splits actieve en afgeronde toernooien. Afgeronde
               krijgen een aparte sectie onderaan én worden gedimd gerenderd. */
            (() => {
              const isCompleted = t => t.status === 'completed' || t.status === 'cancelled'
              // Fase-volgorde: open → live → in voorbereiding → klaar → geannuleerd.
              // Binnen elke bucket: eerstvolgende start eerst (asc); zonder
              // start_time komen achteraan.
              const faseRank = { registration: 0, in_progress: 1, pending: 2, completed: 3, cancelled: 4 }
              const sorteer = arr => [...arr].sort((a, b) => {
                const ra = faseRank[a.status] ?? 9
                const rb = faseRank[b.status] ?? 9
                if (ra !== rb) return ra - rb
                const ta = a.start_time ? new Date(a.start_time).getTime() : Infinity
                const tb = b.start_time ? new Date(b.start_time).getTime() : Infinity
                if (ta !== tb) return ta - tb
                return (b.id || 0) - (a.id || 0)
              })
              const actief   = sorteer(selectedTab === 'all' ? tournaments.filter(t => !isCompleted(t)) : tournaments)
              const afgerond = sorteer(selectedTab === 'all' ? tournaments.filter(isCompleted)          : [])
              const alleGedimd = selectedTab === 'completed'
              return (
                <div className="space-y-3">
                  <LeaderboardPreview />
                  {actief.map(tournament => (
                    <TournamentCardKabinet
                      key={tournament.id}
                      tournament={tournament}
                      currentUser={currentUser}
                      onViewDetails={handleViewDetails}
                      onRegisterClick={handleRegisterClick}
                      dimmed={alleGedimd}
                    />
                  ))}
                  {afgerond.length > 0 && (
                    <div className="pt-8">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px flex-1 bg-border" />
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          Afgerond · {afgerond.length}
                        </div>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-3">
                        {afgerond.map(tournament => (
                          <TournamentCardKabinet
                            key={tournament.id}
                            tournament={tournament}
                            currentUser={currentUser}
                            onViewDetails={handleViewDetails}
                            onRegisterClick={handleRegisterClick}
                            dimmed
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })()
          )}
        </TabsContent>
      </Tabs>

      {/* Register Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-[425px] border-neon-green/30">
          <form onSubmit={handleRegisterSubmit}>
            <DialogHeader>
              <DialogTitle className="text-live">Inschrijven voor toernooi</DialogTitle>
              <DialogDescription>
                {selectedTournament?.name && (
                  <>
                    Meedoen aan <span className="font-medium">{selectedTournament.name}</span>
                    {selectedTournament.type === 'pending' && (
                      <span className="block mt-1 text-xs text-signal">
                        Het format wordt pas gekozen zodra de organisator weet hoeveel spelers er zijn.
                      </span>
                    )}
                    {selectedTournament.start_time && (
                      <span className="block mt-1 text-xs text-muted-foreground">
                        Start: {new Date(selectedTournament.start_time).toLocaleString('nl-BE', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    )}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {registerError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{registerError}</AlertDescription>
                </Alert>
              )}
              {registerSuccess && (
                <Alert className="border-neon-green/30 bg-neon-green/10">
                  <AlertDescription className="text-live">
                    Ingeschreven! Doorschakelen…
                  </AlertDescription>
                </Alert>
              )}
              {/* Bij team-toernooi: verplicht een persistent team kiezen — geen vrije invoer */}
              {selectedTournament?.registration_type === 'team' && (
                <div className="grid gap-2">
                  <label htmlFor="pick_team" className="text-sm font-medium">
                    Team *
                    {selectedTournament.team_size && (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-acid">
                        · exact {selectedTournament.team_size} spelers
                      </span>
                    )}
                  </label>
                  {existingTeams.length > 0 ? (
                    <>
                      <select id="pick_team"
                              value={selectedTeamId ?? ''}
                              onChange={(e) => kiesBestaandTeam(e.target.value ? Number(e.target.value) : null)}
                              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                              disabled={registerLoading || registerSuccess}>
                        <option value="">— Kies je team —</option>
                        {existingTeams.map(t => (
                          <option key={t.id} value={t.id}>{t.name} · {t.members_count} leden</option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Team niet in de lijst? <Link to="/teams" className="text-acid hover:underline">Maak er eerst een aan →</Link>
                      </p>
                    </>
                  ) : (
                    <div className="rounded-md border border-dashed border-border p-4 text-center">
                      <p className="text-sm text-muted-foreground mb-2">
                        Er zijn nog geen teams aangemaakt.
                      </p>
                      <Button asChild variant="outline" size="sm">
                        <Link to="/teams"><Users className="h-4 w-4 mr-1.5" /> Naar Teams-module</Link>
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Personal-toernooi: geen display-name meer, we gebruiken je username automatisch. */}
              {selectedTournament?.registration_type !== 'team' && (
                <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                  Je wordt ingeschreven als <span className="font-semibold">{currentUser?.display_name || currentUser?.username}</span>.
                </div>
              )}

              {/* Team Member Selection for Team Tournaments */}
              {selectedTournament?.registration_type === 'team' && (
                <div className="grid gap-3">
                  <div>
                    <label htmlFor="user_search" className="text-sm font-medium">
                      {selectedTeamId ? 'Kies wie effectief speelt' : 'Team-leden *'}
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">
                      {selectedTeamId
                        ? `Vink de leden van ${teamName} aan die aan dit toernooi meedoen.`
                        : 'Zoek en voeg team-leden toe aan je line-up'}
                    </p>
                  </div>

                  {/* Bij bestaand team: toon team-leden als selectable list, verberg vrije search */}
                  {selectedTeamId ? (
                    <div className="border border-border rounded-md divide-y divide-border/40">
                      {teamsMembersOfPicked.length === 0 && (
                        <div className="p-3 text-sm text-muted-foreground">
                          Dit team heeft nog geen leden. Voeg ze eerst toe via de Teams-module.
                        </div>
                      )}
                      {teamsMembersOfPicked.map(m => {
                        const gekozen = teamMembers.some(tm => tm.id === m.user_id)
                        // Blokkeer users die al elders in dit toernooi zitten (via ander team of solo).
                        const bezet = alIngeschreven.ids.has(m.user_id) && !gekozen
                        const bezetIn = alIngeschreven.byUser?.[m.user_id]
                        return (
                          <button key={m.membership_id} type="button" disabled={bezet}
                                  onClick={() => {
                                    if (gekozen) handleRemoveTeamMember(m.user_id)
                                    else handleAddTeamMember({ id: m.user_id, username: m.username, display_name: m.display_name })
                                  }}
                                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                    bezet ? 'opacity-50 cursor-not-allowed' :
                                    gekozen ? 'bg-neon-green/10' : 'hover:bg-accent'}`}>
                            <div className={`h-4 w-4 rounded-[3px] border grid place-items-center ${
                              gekozen ? 'bg-neon-green border-neon-green' : 'border-border'
                            }`}>
                              {gekozen && <Check className="h-3 w-3 text-background" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">{m.display_name || m.username}</div>
                              <div className="text-xs text-muted-foreground">@{m.username}
                                {m.role === 'captain' && ' · kapitein'}
                              </div>
                            </div>
                            {bezet && (
                              <Badge variant="secondary" className="text-[10px] shrink-0">
                                al in {bezetIn || 'ander team'}
                              </Badge>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <>
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="user_search"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Zoek deelnemer op gebruikersnaam..."
                      disabled={registerLoading || registerSuccess}
                      className="pl-9"
                    />
                  </div>

                  {/* Search Results */}
                  {userSearchResults.length > 0 && (
                    <div className="border border-border rounded-md max-h-48 overflow-y-auto">
                      {userSearchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleAddTeamMember(user)}
                          disabled={teamMembers.find(m => m.id === user.id)}
                          className="w-full px-3 py-2 text-left hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                        >
                          <div>
                            <div className="text-sm font-medium">{user.username}</div>
                            {user.display_name && (
                              <div className="text-xs text-muted-foreground">{user.display_name}</div>
                            )}
                          </div>
                          {teamMembers.find(m => m.id === user.id) && (
                            <Badge variant="secondary" className="text-xs">Toegevoegd</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                    </>
                  )}

                  {/* Selected Team Members */}
                  {teamMembers.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">
                        Geselecteerde leden ({teamMembers.length})
                      </div>
                      <div className="space-y-1">
                        {teamMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-2 rounded-md bg-neon-green/5 border border-neon-green/20"
                          >
                            <div>
                              <div className="text-sm font-medium">{member.username}</div>
                              {member.display_name && (
                                <div className="text-xs text-muted-foreground">{member.display_name}</div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveTeamMember(member.id)}
                              disabled={registerLoading || registerSuccess}
                              className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Team Logo and Banner Upload - for team tournaments */}
              {selectedTournament?.registration_type === 'team' && (
                <div className="grid gap-4 pt-4 border-t border-border">
                  <div>
                    <label className="text-sm font-medium">Team-afbeeldingen (optioneel)</label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Upload een logo en banner om je team-uitstraling te personaliseren.
                    </p>
                  </div>

                  {/* Team Logo Upload */}
                  <div className="grid gap-2">
                    <label htmlFor="team_logo" className="text-sm font-medium flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Team-logo
                    </label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="team_logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSelect}
                        disabled={registerLoading || registerSuccess}
                        className="flex-1"
                      />
                      {logoPreview && (
                        <div className="relative h-12 w-12 rounded border border-neon-green/30 overflow-hidden flex-shrink-0">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTeamLogo(null)
                              setLogoPreview(null)
                            }}
                            disabled={registerLoading || registerSuccess}
                            className="absolute top-0 right-0 bg-destructive/90 hover:bg-destructive text-white p-1 rounded-bl"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Output: 150x150px vierkant • Max: 5MB • Formaten: JPG, PNG, GIF, WebP
                    </p>
                  </div>

                  {/* Team Banner Upload */}
                  <div className="grid gap-2">
                    <label htmlFor="team_banner" className="text-sm font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Team-banner
                    </label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="team_banner"
                        type="file"
                        accept="image/*"
                        onChange={handleBannerSelect}
                        disabled={registerLoading || registerSuccess}
                        className="flex-1"
                      />
                      {bannerPreview && (
                        <div className="relative h-12 w-20 rounded border border-neon-green/30 overflow-hidden flex-shrink-0">
                          <img
                            src={bannerPreview}
                            alt="Banner preview"
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTeamBanner(null)
                              setBannerPreview(null)
                            }}
                            disabled={registerLoading || registerSuccess}
                            className="absolute top-0 right-0 bg-destructive/90 hover:bg-destructive text-white p-1 rounded-bl"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Output: 600x200px (3:1) • Max: 5MB • Formaten: JPG, PNG, GIF, WebP
                    </p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRegisterDialogOpen(false)}
                disabled={registerLoading || registerSuccess}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={registerLoading || registerSuccess}
                className="bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
              >
                {registerLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Bezig met inschrijven…
                  </>
                ) : (
                  'Inschrijving bevestigen'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tournament Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[700px] border-neon-pink/30 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-neon-pink flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              {selectedTournament?.name}
            </DialogTitle>
            <DialogDescription>{selectedTournament?.game}</DialogDescription>
          </DialogHeader>
          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-neon-pink" />
            </div>
          ) : tournamentDetails ? (
            <div className="space-y-6">
              {/* Tournament Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={`border ${getStatusBadgeClass(tournamentDetails.tournament.status)}`}>
                    {tournamentDetails.tournament.status.replace('_', ' ')}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {formatType(tournamentDetails.tournament.type)}
                  </span>
                </div>
                {tournamentDetails.tournament.description && (
                  <p className="text-sm">{tournamentDetails.tournament.description}</p>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Participants:</span>
                    <p className="font-medium">
                      {tournamentDetails.participants.length}
                      {tournamentDetails.tournament.max_participants
                        ? ` / ${tournamentDetails.tournament.max_participants}`
                        : ''}
                    </p>
                  </div>
                  {tournamentDetails.tournament.start_time && (
                    <div>
                      <span className="text-muted-foreground">Start Time:</span>
                      <p className="font-medium">
                        {formatDate(tournamentDetails.tournament.start_time)}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created By:</span>
                    <p className="font-medium">{tournamentDetails.tournament.created_by_name}</p>
                  </div>
                </div>
              </div>

              {/* Participants List */}
              {tournamentDetails.participants.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Registered {tournamentDetails.tournament.registration_type === 'team' ? 'Teams' : 'Players'}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {tournamentDetails.participants.map((participant, index) => (
                      <div
                        key={participant.id}
                        className="flex flex-col gap-1 p-2 rounded-md bg-muted/50 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">#{index + 1}</span>
                          <span className="font-medium">{participant.team_name}</span>
                        </div>
                        {/* Show team members for team tournaments */}
                        {participant.team_members && participant.team_members.length > 0 && (
                          <div className="ml-6 space-y-0.5">
                            {participant.team_members.map((member) => (
                              <div key={member.user_id} className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                <span>{member.display_name || member.username}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Matches */}
              {tournamentDetails.matches.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Matches</h4>
                  <div className="space-y-2">
                    {tournamentDetails.matches.map((match) => (
                      <div
                        key={match.id}
                        className="p-3 rounded-md border border-border bg-card"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">
                            Round {match.round} - Match {match.match_number}
                          </span>
                          {match.winner_name && (
                            <Badge className="bg-neon-green/10 text-neon-green border-neon-green/30">
                              Winner: {match.winner_name}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span>{match.participant1_name || 'TBD'}</span>
                          <span className="text-muted-foreground">vs</span>
                          <span>{match.participant2_name || 'TBD'}</span>
                        </div>
                        {match.score && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Score: {match.score}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to load tournament details</AlertDescription>
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Cropper Dialogs */}
      {originalLogoImage && (
        <ImageCropper
          open={logoCropperOpen}
          onClose={() => {
            setLogoCropperOpen(false)
            setOriginalLogoImage(null)
          }}
          imageSrc={originalLogoImage}
          onCropComplete={handleLogoCropComplete}
          aspectRatio={1}
          shape="rect"
          outputSize={{ width: 150, height: 150 }}
          title="Team-logo bijsnijden"
        />
      )}

      {originalBannerImage && (
        <ImageCropper
          open={bannerCropperOpen}
          onClose={() => {
            setBannerCropperOpen(false)
            setOriginalBannerImage(null)
          }}
          imageSrc={originalBannerImage}
          onCropComplete={handleBannerCropComplete}
          aspectRatio={3}
          shape="rect"
          outputSize={{ width: 600, height: 200 }}
          title="Team-banner bijsnijden"
        />
      )}
    </div>
  )
}

/* Compact leaderboard-preview: top-3 podium + link naar volledig. */
function LeaderboardPreview() {
  const [top, setTop] = useState([])
  useEffect(() => {
    statsAPI.getLeaderboard({ sortBy: 'total_points', limit: 3 })
      .then(r => setTop((r.data.leaderboard || []).slice(0, 3)))
      .catch(() => setTop([]))
  }, [])
  if (top.length === 0) return null
  const tones = [
    { color: 'rgb(var(--gold-rgb))',   label: '#1', Icon: Crown },
    { color: 'rgb(var(--silver-rgb))', label: '#2', Icon: Trophy },
    { color: 'rgb(var(--peach-rgb))',  label: '#3', Icon: Trophy },
  ]
  return (
    <Card className="mb-4" style={{ borderLeft: '2px solid rgb(var(--gold-rgb))' }}>
      <CardContent className="p-4 flex items-center gap-4 flex-wrap">
        <div className="flex-shrink-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gold mb-1">All-time top-3</div>
          <div className="text-xs text-muted-foreground">Volledige leaderboard →</div>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-3">
          {top.map((p, i) => {
            const t = tones[i] || tones[2]
            const Icon = t.Icon
            return (
              <div key={p.user_id} className="flex items-center gap-2 min-w-0">
                <Icon className="h-4 w-4 shrink-0" style={{ color: t.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-muted-foreground">{t.label}</div>
                  <div className="text-sm font-semibold truncate">{p.display_name || p.username}</div>
                </div>
                <div className="font-mono text-sm tabular-nums text-acid">{p.total_points}</div>
              </div>
            )
          })}
        </div>
        <Link to="/tournaments/leaderboard" className="ml-auto text-xs font-mono text-acid hover:underline">
          Alles →
        </Link>
      </CardContent>
    </Card>
  )
}

/* ==================================================================
   TournamentCardKabinet — nieuwe rendering per tournament (mockup C)
================================================================== */
function TournamentCardKabinet({ tournament, currentUser, onViewDetails, onRegisterClick, dimmed = false }) {
  const t = tournament
  const isOpen     = t.status === 'registration'
  const isLive     = t.status === 'in_progress'
  const isDone     = t.status === 'completed'
  const isCanceled = t.status === 'cancelled'

  // Één kleur per status → zowel de linker-borderstreep als de status-pil
  // gebruiken deze zodat de gebruiker de fase in één oogopslag herkent.
  const accent = isLive     ? 'var(--acid-rgb)'
             : isOpen       ? 'var(--live-rgb)'
             : isDone       ? 'var(--gold-rgb)'
             : isCanceled   ? 'var(--signal-rgb)'
             :                'var(--peach-rgb)'  // pending

  const statusPill = isLive
    ? { text: 'Live',        className: 'border-acid bg-acid/8 text-acid' }
    : isDone
      ? { text: 'Klaar',     className: 'border-gold/40 bg-gold/8 text-gold' }
      : isCanceled
        ? { text: 'Geannuleerd', className: 'border-signal/40 bg-signal/8 text-signal' }
        : isOpen
          ? { text: 'Open',  className: 'border-live/40 bg-live/8 text-live' }
          : { text: 'In voorbereiding', className: 'border-peach/40 bg-peach/8 text-peach' }

  const features = t.features || {}
  const flagChips = [
    features.predictions   && { label: 'Predictions', tone: 'text-acid' },
    features.auto_prize    && { label: 'Prijs',       tone: 'text-gold' },
    features.badges        && { label: 'Badge',       tone: 'text-peach' },
    features.points_award  && { label: 'Punten',      tone: 'text-acid' },
    features.check_in      && { label: 'Check-in',    tone: 'text-muted-foreground' },
  ].filter(Boolean)

  const startTime = t.start_time && new Date(t.start_time)
  const now = new Date()
  const startIn = startTime && startTime > now
    ? Math.floor((startTime - now) / 60000)
    : null

  return (
    <div
      className={`rounded-md border p-4 transition-all hover:border-acid group cursor-pointer bg-card border-border ${dimmed ? 'opacity-55 grayscale-[0.6] hover:opacity-90 hover:grayscale-0' : ''}`}
      style={{ borderLeft: `3px solid rgb(${accent})` }}
      onClick={() => onViewDetails(t)}
    >
      <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4">
        <div className="w-11 h-11 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
          <Trophy className={`h-5 w-5 ${isLive ? 'text-acid' : 'text-muted-foreground'} group-hover:text-acid transition-colors`} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-base leading-tight truncate">{t.name}</div>
          <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
            {t.game || 'geen game'} · {formatTypeLabel(t.type)} · {t.registration_type}
            {t.max_participants ? ` · max ${t.max_participants}` : ''}
          </div>
          {t.description && !isLive && (
            <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{t.description}</div>
          )}
          {flagChips.length > 0 && (
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {flagChips.map(c => (
                <span key={c.label} className={`font-mono text-[10px] uppercase tracking-widest ${c.tone}`}>
                  · {c.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <div className="text-sm font-mono tabular-nums text-muted-foreground">
            {(t.participant_count || 0)}{t.max_participants ? `/${t.max_participants}` : ''} deeln.
          </div>
          {startTime && startIn !== null && (
            <div className="text-xs font-mono text-muted-foreground">
              start {startIn > 60 ? `${Math.floor(startIn / 60)}u` : `${startIn}m`}
            </div>
          )}
          {startTime && startIn === null && (
            <div className="text-xs font-mono text-muted-foreground">
              {startTime.toLocaleDateString('nl-BE')}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] tracking-wide ${statusPill.className}`}>
            {isLive && <span className="w-1.5 h-1.5 rounded-full bg-acid" />}
            {statusPill.text}
          </span>
          {t.status === 'registration' && currentUser && (
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onRegisterClick(t) }}>
              Deelnemen
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
